import { useRef } from "react";
import {
  CheckboxCustomEvent,
  IonButton,
  IonCheckbox,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
} from "@ionic/react";
import { GradeModel } from "model";
import { Colors } from "swing-components";

import { translate } from "~utils";

import { ModalHeader } from "../ModalHeader";
import { SelectItem } from "../SelectItem";
import styles from "./GradeField.module.css";
import { useGrades } from "./gradeHooks";

type GradeFieldProps = {
  testData?: GradeModel[]; // optional field for testing and storybook use only
  initialGradeSelected?: string[];
  onChange: (item: string[]) => void;
  isDisabled?: boolean;
};

const UNKNOWN_GRADE_KEY = "unknown-grade";

export function GradeField(props: GradeFieldProps) {
  const { testData, initialGradeSelected = [], onChange, isDisabled } = props;
  const { selected, setSelected, validatedInitialData, gradesData } = useGrades(
    initialGradeSelected,
    testData,
  );
  const gradeModal = useRef<HTMLIonModalElement>(null);

  const isChecked = (value: GradeModel) => {
    return selected.find((item) => item === value.label) !== undefined;
  };

  const checkboxChange = (e: CheckboxCustomEvent) => {
    const { checked, value } = e.detail;
    return checked
      ? setSelected((selected) => [...selected, value.label])
      : setSelected(selected.filter((item) => item !== value.label));
  };

  const onModalDismiss = () => {
    setSelected(validatedInitialData);
    gradeModal.current?.dismiss();
  };

  const onModalConfirm = () => {
    onChange(selected);
    gradeModal.current?.dismiss();
  };

  const isItemDisabled = (item: GradeModel) => {
    return (
      (selected.includes(translate("UNKNOWN")) && item.key !== UNKNOWN_GRADE_KEY) ||
      (!selected.includes(translate("UNKNOWN")) &&
        selected.length !== 0 &&
        item.key === UNKNOWN_GRADE_KEY)
    );
  };

  if (!gradesData) {
    return <div></div>;
  }

  const stringifyGradeGroup = (gradeGroup: GradeModel[]) => {
    return gradeGroup.map((item) => item.label);
  };

  const allItemsInGroup = (groupNumber: number) => {
    return stringifyGradeGroup(gradesData.filter((item) => item.group === groupNumber));
  };

  const isEntireGroupSelected = (groupNumber: number) => {
    const groupedItems = allItemsInGroup(groupNumber);
    return groupedItems.every((item: string) => selected.includes(item));
  };

  const onGradeGroupSelect = (groupNumber: number) => {
    const newSelection = allItemsInGroup(groupNumber);

    if (isEntireGroupSelected(groupNumber)) {
      setSelected((old) => {
        return old.filter((item) => !newSelection.includes(item));
      });
    } else {
      setSelected((old) => {
        return [...old, ...newSelection];
      });
    }
  };

  const onSelectAllClick = () => {
    setSelected(stringifyGradeGroup(gradesData.filter((item) => item.group)));
  };

  const onClearAllClick = () => {
    setSelected([]);
  };

  return (
    <SelectItem
      id="grade-select-modal"
      label={translate("LABEL_GRADE")}
      value={selected.join(", ")}
      stacked
      isDisabled={isDisabled}
      css={isDisabled ? { color: Colors.slate300, cursor: "not-allowed" } : undefined}
    >
      <IonModal
        aria-label="Grade Selection"
        trigger="grade-select-modal"
        ref={gradeModal}
        backdropDismiss={false}
        className="modal-600 stackedModal"
      >
        <ModalHeader
          title={translate("GRADE_MODAL_HEADER_LABEL")}
          onModalDismiss={onModalDismiss}
          onModalConfirm={onModalConfirm}
        />
        <IonContent>
          <IonList lines="none">
            <IonItem>
              <div className="w-full pt-4">
                <IonLabel className={styles.header}>{translate("GRADE_MODAL_SELECT_BY")}</IonLabel>
                <div className={styles.buttonHolder}>
                  <IonButton
                    className={styles.gradeSelectButton}
                    fill={isEntireGroupSelected(1) ? "solid" : "outline"}
                    onClick={() => onGradeGroupSelect(1)}
                  >
                    {translate("GRADE_MODAL_BUTTON_1")}
                  </IonButton>
                  <IonButton
                    className={styles.gradeSelectButton}
                    fill={isEntireGroupSelected(2) ? "solid" : "outline"}
                    onClick={() => onGradeGroupSelect(2)}
                  >
                    {translate("GRADE_MODAL_BUTTON_2")}
                  </IonButton>
                  <IonButton
                    className={styles.gradeSelectButton}
                    fill={isEntireGroupSelected(3) ? "solid" : "outline"}
                    onClick={() => onGradeGroupSelect(3)}
                  >
                    {translate("GRADE_MODAL_BUTTON_3")}
                  </IonButton>
                </div>
              </div>
            </IonItem>

            <IonItem>
              <div className="flex justify-between w-full">
                <div className={styles.header}>{translate("LABEL_GRADE")}</div>
                <div>
                  <span className={styles.select} onClick={() => onSelectAllClick()}>
                    {translate("GRADE_MODAL_SELECT_ALL")}
                  </span>{" "}
                  /{" "}
                  <span className={styles.select} onClick={() => onClearAllClick()}>
                    {translate("GRADE_MODAL_CLEAR_ALL")}
                  </span>
                </div>
              </div>
            </IonItem>
            {gradesData.map((item: GradeModel, idx: number) => {
              return (
                <IonItem key={idx} lines="inset">
                  <IonCheckbox
                    labelPlacement="end"
                    justify="start"
                    value={item}
                    checked={isChecked(item)}
                    onIonChange={checkboxChange}
                    disabled={isItemDisabled(item)}
                  >
                    {item.label}
                  </IonCheckbox>
                </IonItem>
              );
            })}
          </IonList>
        </IonContent>
      </IonModal>
    </SelectItem>
  );
}
